import { AudioRecorder } from "@components/audio-recorder";
import { FileInput } from "@components/file-input";
import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useSavedState } from "@features/utils/use-saved-state";
import { XMarkIcon } from "@heroicons/react/16/solid";
import {
  Box,
  Button,
  Heading,
  SegmentedControl,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type SoundMode = "no-sound" | "with-sound";
type Quality = "low" | "medium" | "high";

interface StudioCreateVideoProps {
  image: ImagesType;
}

/**
 * Cost:
 * 0.015/s for low quality speech + no audio video (lipsync)
 * 0.04/s for low quality video (no audio)
 *
 * Total low quality audio = 0.015+0.04 = 0.055/s. A 1m video will be 2x 8s videos edited to make 1m, and 1m of lip sync.
 * So total for 1m -> 8*2*0.04 + 60*0.015 = 0.64 + 0.9 = 1.54 credits per minute of low quality video with audio.
 * For 5 minutes -> 8*2*0.04 + 5*60*0.015 = ~$5
 *
 * High quality
 *
 * 0.0005/s for subtitles
 */

// Credits per second of Seedance 2.0 Fast video, keep in sync with the backend
const SEEDANCE_CREDITS_PER_SECOND = 13;

export const estimateCost = (
  quality: Quality,
  duration: number,
  audio?: boolean,
  nsfw?: boolean
): number => {
  if (!audio) {
    return (
      // Veo 3 / Veo 3 fast / (Seedance 2.0 Fast or Wan2.2)
      // Uncensored: Wan2.2
      duration *
      (quality === "low" && !nsfw
        ? SEEDANCE_CREDITS_PER_SECOND
        : quality === "high"
          ? 20
          : quality === "medium"
            ? 10
            : 5)
    );
  }

  if (duration <= 8) {
    return (
      // Veo 3 or Wan2.5 / Veo 3 fast / Wan2.5 480p
      // Unsensored: Wan2.5 and play on output quality
      duration * (quality === "high" ? 40 : quality === "medium" ? 15 : 5)
    );
  }

  // Lipsync mode, the base video is always Seedance
  const videoCost = Math.max(
    estimateCost(quality, 16, false, nsfw), // 2x 8s videos
    16 * SEEDANCE_CREDITS_PER_SECOND
  );
  const audioCost = duration * 1.5;
  return videoCost + audioCost;
};

export const StudioCreateVideo = ({ image }: StudioCreateVideoProps) => {
  const { t } = useTranslation();
  const { createVideo } = useImage(image.id);

  const [count, setCount] = useSavedState("video-count", 1);
  const [prompt, setPrompt] = useSavedState("video-prompt-" + image.id, "", {
    ttlMs: 7 * 86400000,
  }); // 7 day
  const [soundMode, setSoundMode] = useSavedState<SoundMode>(
    "video-sound-mode",
    "no-sound"
  );
  const [quality, setQuality] = useSavedState<Quality>("video-quality", "low");
  const [duration, setDuration] = useSavedState("video-duration", 5);
  const [isProcessing, setIsProcessing] = useState(false);

  // Audio state
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  // Effective duration: audio duration if available, otherwise selected duration
  const effectiveDuration = audioDuration ?? duration;

  // Helper to get audio file duration
  const getAudioFileDuration = (file: File | Blob): Promise<number> => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => {
        resolve(Math.ceil(audio.duration + 0.1));
        URL.revokeObjectURL(audio.src);
      });
      audio.addEventListener("error", () => {
        resolve(0);
        URL.revokeObjectURL(audio.src);
      });
      console.log("Setting audio src for duration check");
      audio.src = URL.createObjectURL(file);
    });
  };

  const fileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const handleGenerate = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      let audioBase64: string | undefined | boolean;

      if (soundMode === "with-sound") {
        if (recordedAudio) {
          audioBase64 = await fileToBase64(recordedAudio);
        } else if (audioFile) {
          audioBase64 = await fileToBase64(audioFile);
        } else {
          audioBase64 = true;
        }
      }

      await createVideo({
        prompt: prompt.trim() || undefined,
        audio: audioBase64,
        quality,
        duration: effectiveDuration,
      });
    } catch (error) {
      console.error("Failed to create video:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecordingComplete = async (blob: Blob) => {
    console.log("Recording complete, duration:");
    setRecordedAudio(blob);
    const fileDuration = await getAudioFileDuration(blob);
    setAudioDuration(fileDuration > 0 ? fileDuration : null);
    setAudioFile(null);
  };

  const handleAudioFileChange = async (file: File | string | null) => {
    if (file instanceof File) {
      setAudioFile(file);
      setRecordedAudio(null); // Clear recording if file uploaded
      const fileDuration = await getAudioFileDuration(file);
      setAudioDuration(fileDuration > 0 ? fileDuration : null);
    } else {
      setAudioFile(null);
      setAudioDuration(null);
    }
  };

  const getCredits = () => {
    return Math.ceil(
      estimateCost(
        quality,
        effectiveDuration,
        soundMode === "with-sound",
        image.nsfw
      )
    );
  };

  return (
    <div className="space-y-4">
      <Heading size="3">{t("studio.actions.video.title")}</Heading>

      <div className="space-y-3">
        {/* Prompt input */}
        <TextArea
          placeholder={t("studio.actions.video.prompt_placeholder")}
          value={prompt}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setPrompt(e.currentTarget.value)
          }
          rows={3}
        />

        {/* Sound options */}
        <Box className="p-2 bg-slate-100 dark:bg-slate-800 rounded-md space-y-3">
          <SegmentedControl.Root
            className="bg-slate-200 dark:bg-slate-900"
            value={soundMode}
            onValueChange={(value) => setSoundMode(value as SoundMode)}
            size="1"
          >
            <SegmentedControl.Item value="no-sound">
              {t("studio.actions.video.no_sound")}
            </SegmentedControl.Item>
            <SegmentedControl.Item value="with-sound">
              {t("studio.actions.video.with_sound")}
            </SegmentedControl.Item>
          </SegmentedControl.Root>

          {soundMode === "with-sound" && (
            <div className="space-y-3">
              {/* Audio recorder */}
              <div className="space-y-2">
                <Text size="2" weight="medium">
                  {t("studio.actions.video.record_audio")}
                </Text>
                <AudioRecorder
                  onRecordingComplete={handleRecordingComplete}
                  onClear={() => {
                    setRecordedAudio(null);
                    setAudioDuration(null);
                  }}
                  disabled={isProcessing}
                />
              </div>

              {/* Or upload audio file */}
              {!recordedAudio && (
                <div className="space-y-2">
                  <Text size="2" weight="medium">
                    {t("studio.actions.video.or_upload_audio")}
                  </Text>
                  <Box className="w-full rounded-md border border-dashed border-slate-500 p-2">
                    {audioFile ? (
                      <div className="flex items-center justify-between">
                        <Text size="2" className="truncate">
                          {audioFile.name}
                          {audioDuration && (
                            <span className="text-slate-500 ml-2">
                              ({audioDuration}s)
                            </span>
                          )}
                        </Text>
                        <Button
                          size="1"
                          color="red"
                          variant="soft"
                          onClick={() => {
                            setAudioFile(null);
                            setAudioDuration(null);
                          }}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <FileInput
                        value={null}
                        onChange={handleAudioFileChange}
                        accept="audio/wav,audio/mp3"
                        className="border-none p-0"
                      />
                    )}
                  </Box>
                </div>
              )}
            </div>
          )}

          {!recordedAudio && !audioFile && (
            <div className="space-y-1">
              <Text size="1" color="gray" className="block">
                {t("studio.actions.video.duration")}
              </Text>
              <ResponsiveSelect
                size="2"
                value={duration.toString()}
                onChange={(value) => setDuration(Number(value))}
                items={[
                  { value: "4", label: "4s" },
                  { value: "8", label: "8s" },
                ]}
              />
            </div>
          )}
        </Box>

        {/* Quality */}
        <div className="space-y-1">
          <Text size="1" color="gray" className="block">
            {t("studio.actions.video.quality")}
          </Text>
          <ResponsiveSelect
            className="w-full"
            size="2"
            value={quality}
            onChange={(value) => setQuality(value as Quality)}
            items={[
              {
                value: "low",
                label: t("studio.actions.video.quality_low"),
              },
              {
                value: "medium",
                label: t("studio.actions.video.quality_medium"),
              },
              {
                value: "high",
                label: t("studio.actions.video.quality_high"),
              },
            ]}
          />
        </div>

        {/* Generate button */}

        <div className="flex space-x-2 w-full">
          <ResponsiveSelect
            size="2"
            value={count.toString()}
            onChange={(value) => setCount(Number(value))}
            items={[
              { value: "1", label: "1" },
              { value: "2", label: "2" },
              { value: "3", label: "3" },
              { value: "4", label: "4" },
            ]}
            renderTrigger={() => (
              <span className="text-xs uppercase text-gray-500">{count}</span>
            )}
          />
          <div className="grow">
            <UpgradeNeeded minimumCredits={count * getCredits()}>
              <Button
                onClick={handleGenerate}
                disabled={isProcessing || !prompt.trim()}
                loading={isProcessing}
                className="w-full"
                color="purple"
              >
                {t("studio.actions.video.generate")}{" "}
                <CoinsVal val={count * getCredits()} />
              </Button>
            </UpgradeNeeded>
          </div>
        </div>

        <Text size="1" color="gray" className="block">
          {t("studio.actions.video.description")}
        </Text>
      </div>
    </div>
  );
};
